import React from "react";
import { useSelector } from "react-redux";
import { Loader2, CheckCircle2, AlertCircle, X, RotateCw } from "lucide-react";

import { Progress } from "./ui/progress";
import { retryUpload, dismissUpload } from "../features/uploadManager/uploadManagerService";

// Global, always-mounted upload tracker. Pinned to the very top of the screen
// so it follows the user everywhere in the portal. Renders one bar per active
// upload (back-to-back submissions stack, each labelled with its task), success
// bars auto-clear, and failed bars stay put with Retry + dismiss (X).
//
// `data-sticky-top` opts this bar into StickyBar's pinned-banner measurement
// (src/components/ui/StickyBar.jsx). It is `fixed`, not `sticky`, but the
// contract is the same one question — "is this element covering the top of the
// screen right now?" — and the answer is yes whenever an upload is running, so
// a page's pinned filter bar parks below it instead of losing its first row
// underneath. (Codex P2 on PR #3057.)
const UploadManagerBar = () => {
  const uploads = useSelector((state) => state.uploadManager.uploads);

  if (!uploads || uploads.length === 0) return null;

  return (
    <div
      data-sticky-top=""
      className="fixed top-0 left-0 right-0 z-[100] flex flex-col gap-1 p-2 pointer-events-none"
    >
      {uploads.map((u) => (
        <div
          key={u.id}
          className={`pointer-events-auto mx-auto w-full max-w-md rounded-lg px-3 py-2 shadow-lg text-white ${
            u.status === "error"
              ? "bg-red-600"
              : u.status === "success"
              ? "bg-green-600"
              : "bg-neutral-800 border border-[#f16637]"
          }`}
        >
          <div className="flex items-center gap-2">
            {u.status === "uploading" && (
              <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin text-[#f16637]" />
            )}
            {u.status === "success" && (
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            )}
            {u.status === "error" && (
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
            )}

            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">
                {u.status === "uploading" && `Uploading — ${u.label}`}
                {u.status === "success" && `Uploaded — ${u.label}`}
                {u.status === "error" && `Upload failed — ${u.label}`}
              </p>
              {u.status === "error" && u.error && (
                <p className="text-[11px] opacity-90 truncate">{u.error}</p>
              )}
            </div>

            {u.status === "uploading" && (
              <span className="text-xs tabular-nums flex-shrink-0">
                {u.progress}%
              </span>
            )}

            {u.status === "error" && (
              <button
                type="button"
                onClick={() => retryUpload(u.id)}
                className="flex items-center gap-1 text-xs font-semibold underline flex-shrink-0"
                aria-label="Retry upload"
              >
                <RotateCw className="w-3.5 h-3.5" />
                Retry
              </button>
            )}

            {/* Error banners are dismiss-on-click only (spec: user must X it).
                Success banners also offer an X so the user can clear early. */}
            {u.status !== "uploading" && (
              <button
                type="button"
                onClick={() => dismissUpload(u.id)}
                className="flex-shrink-0 rounded p-0.5 hover:bg-black/20"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {u.status === "uploading" && (
            <Progress value={u.progress} className="h-1.5 mt-1.5" />
          )}
        </div>
      ))}
    </div>
  );
};

export default UploadManagerBar;
